/** @jsx isolateComponent */
import React, { ReactNode } from 'react';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import {
  Colors,
  ColorValue,
  Typography,
} from '@zapier/design-system/src/theme';

type TagLabelColorVariant = {
  backgroundColor: string;
  color: ColorValue;
};

const TagLabelColorVariants: {
  [x: string]: TagLabelColorVariant;
} = {
  light: {
    backgroundColor: Colors.grey100,
    color: Colors.black,
  },
  dark: {
    backgroundColor: Colors.grey700,
    color: Colors.white,
  },
  blue: {
    backgroundColor: Colors.blue100,
    color: Colors.blue500,
  },
};

type TagLabelSizeVariant = {
  lineHeight: string;
  height: number;
};

const TagLabelSizeVariants: {
  [x: string]: TagLabelSizeVariant;
} = {
  medium: { ...Typography.allCaps800, lineHeight: '20px', height: 20 },
  small: { ...Typography.allCaps700, lineHeight: '15px', height: 15 },
};

type TagLabelProps = {
  /**
   * The color variant of the label.
   *
   * @default 'light'
   * */
  color?: keyof typeof TagLabelColorVariants & string;
  /** Text to render inside the label. */
  children: ReactNode;
  /**
   * The size variant of the label.
   *
   * @default 'medium'
   * */
  size?: keyof typeof TagLabelSizeVariants & string;
};

const defaultProps = {
  color: 'light',
  size: 'medium',
};

const Styles = labelStyles(__filename, {
  root: (_props: TagLabelProps) => {
    const props = {
      ...defaultProps,
      ..._props,
    };
    return [
      {
        display: 'inline-block',
        padding: `0 10px`,
        borderRadius: 5,
        alignItems: 'center',
      },
      withModifier(props.color, TagLabelColorVariants[props.color]),
      withModifier(props.size, TagLabelSizeVariants[props.size]),
    ];
  },
});

/**
 * Renders a tag-like component.
 *
 * Tags: core-ui
 */
const TagLabel: React.FC<TagLabelProps> = props => (
  <span css={Styles.root(props)}>{props.children}</span>
);

TagLabel.defaultProps = defaultProps;

export default TagLabel;
